import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit3,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Send,
  Download,
  Upload,
  RefreshCw,
  Eye,
  Activity,
  Play,
  Pause,
  Zap,
  Wifi,
  WifiOff,
  Key,
  ExternalLink,
  Shield,
  Check,
  X
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getServices,
  getIncidents,
  createIncident,
  addIncidentUpdate,
  deleteIncident,
  exportStatusData,
  importStatusData,
  resetStatusData,
  type Incident,
  type ServiceConfig,
} from '@/services/statusService';
import {
  getMonitoringConfig,
  saveMonitoringConfig,
  triggerHealthCheck,
  startMonitoring,
  stopMonitoring,
  getApiKeys,
  saveApiKeys,
  fetchUptimeRobotStatus,
  fetchBetterUptimeStatus,
  type MonitoringConfig,
  type HealthCheckResult,
  type ExternalApiKeys,
  type UptimeRobotMonitor,
  type BetterUptimeMonitor,
} from '@/services/monitoringService';

const impactOptions = [
  { value: 'minor', label: 'Minor', color: 'text-yellow-500', bg: 'bg-yellow-500/20' },
  { value: 'major', label: 'Major', color: 'text-orange-500', bg: 'bg-orange-500/20' },
  { value: 'critical', label: 'Critical', color: 'text-red-500', bg: 'bg-red-500/20' },
  { value: 'maintenance', label: 'Maintenance', color: 'text-blue-500', bg: 'bg-blue-500/20' },
];

const statusOptions = [
  { value: 'investigating', label: 'Investigating' },
  { value: 'identified', label: 'Identified' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'scheduled', label: 'Scheduled' },
];

export function StatusAdminPage() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [services, setServices] = useState<ServiceConfig[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState<string | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formImpact, setFormImpact] = useState<Incident['impact']>('minor');
  const [formStatus, setFormStatus] = useState<Incident['status']>('investigating');
  const [formServices, setFormServices] = useState<string[]>([]);
  const [formMessage, setFormMessage] = useState('');

  // Update form state
  const [updateStatus, setUpdateStatus] = useState('');
  const [updateMessage, setUpdateMessage] = useState('');

  // Monitoring state
  const [monitoringConfig, setMonitoringConfig] = useState<MonitoringConfig>(getMonitoringConfig());
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckResults, setLastCheckResults] = useState<HealthCheckResult[]>([]);
  const [showMonitoringPanel, setShowMonitoringPanel] = useState(false);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ExternalApiKeys>(getApiKeys());
  const [showApiKeyPanel, setShowApiKeyPanel] = useState(false);
  const [isFetchingExternal, setIsFetchingExternal] = useState(false);
  const [uptimeRobotMonitors, setUptimeRobotMonitors] = useState<UptimeRobotMonitor[]>([]);
  const [betterUptimeMonitors, setBetterUptimeMonitors] = useState<BetterUptimeMonitor[]>([]);
  const [apiKeyTestStatus, setApiKeyTestStatus] = useState<Record<string, 'idle' | 'testing' | 'success' | 'error'>>({});

  const loadData = () => {
    setIncidents(getIncidents());
    setServices(getServices());
    setMonitoringConfig(getMonitoringConfig());
  };

  useEffect(() => {
    loadData();
    // Start monitoring if enabled
    if (monitoringConfig.enabled) {
      startMonitoring(true); // demo mode
    }
    return () => stopMonitoring();
  }, []);

  const handleToggleMonitoring = () => {
    const newConfig = { ...monitoringConfig, enabled: !monitoringConfig.enabled };
    setMonitoringConfig(newConfig);
    saveMonitoringConfig(newConfig);

    if (newConfig.enabled) {
      startMonitoring(true);
    } else {
      stopMonitoring();
    }
  };

  const handleManualCheck = async () => {
    setIsChecking(true);
    try {
      const results = await triggerHealthCheck(true); // demo mode
      setLastCheckResults(results);
      loadData(); // Refresh incidents in case any were created
    } finally {
      setIsChecking(false);
    }
  };

  const handleUpdateMonitoringConfig = (key: keyof MonitoringConfig, value: number | boolean) => {
    const newConfig = { ...monitoringConfig, [key]: value };
    setMonitoringConfig(newConfig);
    saveMonitoringConfig(newConfig);
  };

  // API Key handlers
  const handleSaveApiKey = (service: keyof ExternalApiKeys, value: string) => {
    const newKeys = { ...apiKeys, [service]: value || undefined };
    setApiKeys(newKeys);
    saveApiKeys(newKeys);
    setApiKeyTestStatus(prev => ({ ...prev, [service]: 'idle' }));
  };

  const handleTestApiKey = async (service: keyof ExternalApiKeys) => {
    const key = apiKeys[service];
    if (!key) return;

    setApiKeyTestStatus(prev => ({ ...prev, [service]: 'testing' }));

    try {
      if (service === 'uptimeRobot') {
        const monitors = await fetchUptimeRobotStatus(key);
        if (monitors.length > 0) {
          setUptimeRobotMonitors(monitors);
          setApiKeyTestStatus(prev => ({ ...prev, [service]: 'success' }));
        } else {
          setApiKeyTestStatus(prev => ({ ...prev, [service]: 'error' }));
        }
      } else if (service === 'betterUptime') {
        const monitors = await fetchBetterUptimeStatus(key);
        if (monitors.length > 0) {
          setBetterUptimeMonitors(monitors);
          setApiKeyTestStatus(prev => ({ ...prev, [service]: 'success' }));
        } else {
          setApiKeyTestStatus(prev => ({ ...prev, [service]: 'error' }));
        }
      }
    } catch {
      setApiKeyTestStatus(prev => ({ ...prev, [service]: 'error' }));
    }
  };

  const handleFetchExternalStatus = async () => {
    setIsFetchingExternal(true);
    try {
      const promises: Promise<void>[] = [];

      if (apiKeys.uptimeRobot) {
        promises.push(
          fetchUptimeRobotStatus(apiKeys.uptimeRobot).then(monitors => {
            setUptimeRobotMonitors(monitors);
          })
        );
      }

      if (apiKeys.betterUptime) {
        promises.push(
          fetchBetterUptimeStatus(apiKeys.betterUptime).then(monitors => {
            setBetterUptimeMonitors(monitors);
          })
        );
      }

      await Promise.all(promises);
    } finally {
      setIsFetchingExternal(false);
    }
  };

  const getUptimeRobotStatusLabel = (status: number): { label: string; color: string } => {
    switch (status) {
      case 0: return { label: 'Paused', color: 'text-gray-500' };
      case 1: return { label: 'Not Checked', color: 'text-gray-500' };
      case 2: return { label: 'Up', color: 'text-green-500' };
      case 8: return { label: 'Seems Down', color: 'text-yellow-500' };
      case 9: return { label: 'Down', color: 'text-red-500' };
      default: return { label: 'Unknown', color: 'text-gray-500' };
    }
  };

  const handleCreateIncident = () => {
    if (!formTitle || formServices.length === 0 || !formMessage) return;

    createIncident({
      title: formTitle,
      status: formStatus,
      impact: formImpact,
      affectedServices: formServices,
      createdAt: new Date().toISOString(),
      updates: [{
        id: `upd-${Date.now()}`,
        status: formStatus === 'scheduled' ? 'Scheduled' : 'Investigating',
        message: formMessage,
        timestamp: new Date().toISOString(),
      }],
    });

    // Reset form
    setFormTitle('');
    setFormImpact('minor');
    setFormStatus('investigating');
    setFormServices([]);
    setFormMessage('');
    setShowCreateModal(false);
    loadData();
  };

  const handleAddUpdate = (incidentId: string) => {
    if (!updateMessage || !updateStatus) return;

    addIncidentUpdate(incidentId, {
      status: updateStatus,
      message: updateMessage,
      timestamp: new Date().toISOString(),
    });

    setUpdateStatus('');
    setUpdateMessage('');
    setShowUpdateModal(null);
    loadData();
  };

  const handleDeleteIncident = (id: string) => {
    if (confirm('Are you sure you want to delete this incident?')) {
      deleteIncident(id);
      loadData();
    }
  };

  const handleExport = () => {
    const data = exportStatusData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ochtopus-status-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (importStatusData(content)) {
          alert('Data imported successfully!');
          loadData();
        } else {
          alert('Failed to import data. Please check the file format.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleReset = () => {
    if (confirm('Are you sure you want to reset all data to defaults? This cannot be undone.')) {
      resetStatusData();
      loadData();
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getImpactConfig = (impact: string) => {
    return impactOptions.find(o => o.value === impact) || impactOptions[0];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Status Monitor</h1>
          <p className="text-text-secondary">Manage incidents and system status</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/status" target="_blank" className="btn btn-secondary btn-sm">
            <Eye size={16} />
            View Public Status
          </a>
          <button onClick={handleExport} className="btn btn-secondary btn-sm">
            <Download size={16} />
            Export
          </button>
          <button onClick={handleImport} className="btn btn-secondary btn-sm">
            <Upload size={16} />
            Import
          </button>
          <button onClick={handleReset} className="btn btn-secondary btn-sm text-red-500">
            <RefreshCw size={16} />
            Reset
          </button>
        </div>
      </div>

        {/* Monitoring Panel */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Activity className="text-accent" size={24} />
              <div>
                <h2 className="font-semibold">Automatic Monitoring</h2>
                <p className="text-text-secondary text-sm">Health checks run automatically and create incidents</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleManualCheck}
                disabled={isChecking}
                className="btn btn-secondary btn-sm"
              >
                <Zap size={16} className={isChecking ? 'animate-spin' : ''} />
                {isChecking ? 'Checking...' : 'Run Check Now'}
              </button>
              <button
                onClick={handleToggleMonitoring}
                className={cn(
                  'btn btn-sm',
                  monitoringConfig.enabled ? 'btn-primary' : 'btn-secondary'
                )}
              >
                {monitoringConfig.enabled ? (
                  <>
                    <Pause size={16} />
                    Stop Monitoring
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Start Monitoring
                  </>
                )}
              </button>
              <button
                onClick={() => setShowMonitoringPanel(!showMonitoringPanel)}
                className="btn btn-secondary btn-sm"
              >
                Settings
              </button>
            </div>
          </div>

          {/* Status indicator */}
          <div className="flex items-center gap-2 mb-4">
            {monitoringConfig.enabled ? (
              <>
                <Wifi className="text-green-500 animate-pulse" size={16} />
                <span className="text-green-500 text-sm">Monitoring active - checking every {monitoringConfig.checkInterval / 1000}s</span>
              </>
            ) : (
              <>
                <WifiOff className="text-text-tertiary" size={16} />
                <span className="text-text-tertiary text-sm">Monitoring disabled</span>
              </>
            )}
          </div>

          {/* Last check results */}
          {lastCheckResults.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {lastCheckResults.map(result => (
                <div
                  key={result.serviceId}
                  className={cn(
                    'p-3 rounded-lg border',
                    result.status === 'healthy' && 'bg-green-500/10 border-green-500/30',
                    result.status === 'degraded' && 'bg-yellow-500/10 border-yellow-500/30',
                    result.status === 'down' && 'bg-red-500/10 border-red-500/30'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {result.status === 'healthy' ? (
                      <CheckCircle2 size={14} className="text-green-500" />
                    ) : result.status === 'degraded' ? (
                      <AlertTriangle size={14} className="text-yellow-500" />
                    ) : (
                      <XCircle size={14} className="text-red-500" />
                    )}
                    <span className="text-sm font-medium truncate">
                      {services.find(s => s.id === result.serviceId)?.name || result.serviceId}
                    </span>
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {result.responseTime}ms
                    {result.error && <span className="text-red-400 ml-2">{result.error}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Monitoring settings panel */}
          {showMonitoringPanel && (
            <div className="border-t border-border-default pt-4 mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Check Interval (seconds)</label>
                  <input
                    type="number"
                    value={monitoringConfig.checkInterval / 1000}
                    onChange={(e) => handleUpdateMonitoringConfig('checkInterval', parseInt(e.target.value) * 1000)}
                    min={10}
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Degraded Threshold (ms)</label>
                  <input
                    type="number"
                    value={monitoringConfig.degradedThreshold}
                    onChange={(e) => handleUpdateMonitoringConfig('degradedThreshold', parseInt(e.target.value))}
                    min={100}
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Failures Before Incident</label>
                  <input
                    type="number"
                    value={monitoringConfig.consecutiveFailuresBeforeIncident}
                    onChange={(e) => handleUpdateMonitoringConfig('consecutiveFailuresBeforeIncident', parseInt(e.target.value))}
                    min={1}
                    max={10}
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Successes Before Resolve</label>
                  <input
                    type="number"
                    value={monitoringConfig.consecutiveSuccessesBeforeResolve}
                    onChange={(e) => handleUpdateMonitoringConfig('consecutiveSuccessesBeforeResolve', parseInt(e.target.value))}
                    min={1}
                    max={10}
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={monitoringConfig.autoCreateIncidents}
                    onChange={(e) => handleUpdateMonitoringConfig('autoCreateIncidents', e.target.checked)}
                    className="w-4 h-4 rounded border-border-default"
                  />
                  <span className="text-sm">Auto-create incidents</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={monitoringConfig.autoResolveIncidents}
                    onChange={(e) => handleUpdateMonitoringConfig('autoResolveIncidents', e.target.checked)}
                    className="w-4 h-4 rounded border-border-default"
                  />
                  <span className="text-sm">Auto-resolve incidents</span>
                </label>
              </div>
              <p className="text-text-tertiary text-xs">
                Note: Currently using demo endpoints. Configure real health check URLs in monitoringService.ts for production.
              </p>
            </div>
          )}
        </div>

        {/* External Monitoring Integrations */}
        <div className="card mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Key className="text-accent" size={24} />
              <div>
                <h2 className="font-semibold">External Monitoring Services</h2>
                <p className="text-text-secondary text-sm">Connect to UptimeRobot, Better Uptime, or Pingdom</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleFetchExternalStatus}
                disabled={isFetchingExternal || (!apiKeys.uptimeRobot && !apiKeys.betterUptime)}
                className="btn btn-secondary btn-sm"
              >
                <RefreshCw size={16} className={isFetchingExternal ? 'animate-spin' : ''} />
                Fetch Status
              </button>
              <button
                onClick={() => setShowApiKeyPanel(!showApiKeyPanel)}
                className="btn btn-secondary btn-sm"
              >
                <Shield size={16} />
                API Keys
              </button>
            </div>
          </div>

          {/* API Key Configuration Panel */}
          {showApiKeyPanel && (
            <div className="border-t border-border-default pt-4 mt-4 space-y-4">
              {/* UptimeRobot */}
              <div className="p-4 rounded-lg bg-bg-elevated border border-border-default">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src="https://uptimerobot.com/favicon.ico" alt="UptimeRobot" className="w-5 h-5" />
                    <span className="font-medium">UptimeRobot</span>
                  </div>
                  <a
                    href="https://uptimerobot.com/dashboard#mySettings"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent text-xs flex items-center gap-1 hover:underline"
                  >
                    Get API Key <ExternalLink size={12} />
                  </a>
                </div>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={apiKeys.uptimeRobot || ''}
                    onChange={(e) => handleSaveApiKey('uptimeRobot', e.target.value)}
                    placeholder="ur-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className="flex-1 px-3 py-2 rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none text-sm font-mono"
                  />
                  <button
                    onClick={() => handleTestApiKey('uptimeRobot')}
                    disabled={!apiKeys.uptimeRobot || apiKeyTestStatus.uptimeRobot === 'testing'}
                    className="btn btn-secondary btn-sm min-w-[80px]"
                  >
                    {apiKeyTestStatus.uptimeRobot === 'testing' ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : apiKeyTestStatus.uptimeRobot === 'success' ? (
                      <Check size={14} className="text-green-500" />
                    ) : apiKeyTestStatus.uptimeRobot === 'error' ? (
                      <X size={14} className="text-red-500" />
                    ) : (
                      'Test'
                    )}
                  </button>
                </div>
                {apiKeyTestStatus.uptimeRobot === 'success' && (
                  <p className="text-green-500 text-xs mt-2">Connected! Found {uptimeRobotMonitors.length} monitors.</p>
                )}
                {apiKeyTestStatus.uptimeRobot === 'error' && (
                  <p className="text-red-500 text-xs mt-2">Failed to connect. Check your API key.</p>
                )}
              </div>

              {/* Better Uptime */}
              <div className="p-4 rounded-lg bg-bg-elevated border border-border-default">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <img src="https://betteruptime.com/favicon.ico" alt="Better Uptime" className="w-5 h-5" />
                    <span className="font-medium">Better Uptime</span>
                  </div>
                  <a
                    href="https://betteruptime.com/team/api-tokens"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent text-xs flex items-center gap-1 hover:underline"
                  >
                    Get API Token <ExternalLink size={12} />
                  </a>
                </div>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={apiKeys.betterUptime || ''}
                    onChange={(e) => handleSaveApiKey('betterUptime', e.target.value)}
                    placeholder="Your Better Uptime API token"
                    className="flex-1 px-3 py-2 rounded-lg bg-bg-base border border-border-default focus:border-accent focus:outline-none text-sm font-mono"
                  />
                  <button
                    onClick={() => handleTestApiKey('betterUptime')}
                    disabled={!apiKeys.betterUptime || apiKeyTestStatus.betterUptime === 'testing'}
                    className="btn btn-secondary btn-sm min-w-[80px]"
                  >
                    {apiKeyTestStatus.betterUptime === 'testing' ? (
                      <RefreshCw size={14} className="animate-spin" />
                    ) : apiKeyTestStatus.betterUptime === 'success' ? (
                      <Check size={14} className="text-green-500" />
                    ) : apiKeyTestStatus.betterUptime === 'error' ? (
                      <X size={14} className="text-red-500" />
                    ) : (
                      'Test'
                    )}
                  </button>
                </div>
                {apiKeyTestStatus.betterUptime === 'success' && (
                  <p className="text-green-500 text-xs mt-2">Connected! Found {betterUptimeMonitors.length} monitors.</p>
                )}
                {apiKeyTestStatus.betterUptime === 'error' && (
                  <p className="text-red-500 text-xs mt-2">Failed to connect. Check your API token.</p>
                )}
              </div>

              {/* Pingdom - placeholder */}
              <div className="p-4 rounded-lg bg-bg-elevated border border-border-default opacity-60">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-accent/20 flex items-center justify-center text-xs">P</span>
                    <span className="font-medium">Pingdom</span>
                    <span className="text-xs text-text-tertiary">(Coming Soon)</span>
                  </div>
                </div>
                <input
                  type="password"
                  disabled
                  placeholder="Pingdom API key support coming soon..."
                  className="w-full px-3 py-2 rounded-lg bg-bg-base border border-border-default text-sm font-mono cursor-not-allowed"
                />
              </div>

              <p className="text-text-tertiary text-xs">
                API keys are stored locally in your browser. They are used to fetch real-time status from your monitoring services.
              </p>
            </div>
          )}

          {/* External Monitors Display */}
          {(uptimeRobotMonitors.length > 0 || betterUptimeMonitors.length > 0) && (
            <div className="border-t border-border-default pt-4 mt-4">
              <h3 className="text-sm font-medium mb-3">External Monitor Status</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {uptimeRobotMonitors.map(monitor => {
                  const status = getUptimeRobotStatusLabel(monitor.status);
                  return (
                    <div
                      key={monitor.id}
                      className="p-3 rounded-lg border bg-bg-elevated border-border-default"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={cn(
                          'w-2 h-2 rounded-full',
                          monitor.status === 2 && 'bg-green-500',
                          (monitor.status === 8 || monitor.status === 9) && 'bg-red-500',
                          monitor.status === 0 && 'bg-gray-500'
                        )} />
                        <span className="text-sm font-medium truncate">{monitor.friendly_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={cn('text-xs', status.color)}>{status.label}</span>
                        <span className="text-xs text-text-tertiary">UptimeRobot</span>
                      </div>
                    </div>
                  );
                })}
                {betterUptimeMonitors.map(monitor => (
                  <div
                    key={monitor.id}
                    className="p-3 rounded-lg border bg-bg-elevated border-border-default"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn(
                        'w-2 h-2 rounded-full',
                        monitor.attributes.status === 'up' && 'bg-green-500',
                        monitor.attributes.status === 'down' && 'bg-red-500',
                        monitor.attributes.status === 'paused' && 'bg-gray-500',
                        monitor.attributes.status === 'pending' && 'bg-yellow-500',
                        monitor.attributes.status === 'maintenance' && 'bg-blue-500'
                      )} />
                      <span className="text-sm font-medium truncate">{monitor.attributes.pronounceable_name}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn(
                        'text-xs capitalize',
                        monitor.attributes.status === 'up' && 'text-green-500',
                        monitor.attributes.status === 'down' && 'text-red-500',
                        monitor.attributes.status === 'paused' && 'text-gray-500'
                      )}>{monitor.attributes.status}</span>
                      <span className="text-xs text-text-tertiary">Better Uptime</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold mb-1">Create New Incident</h2>
              <p className="text-text-secondary text-sm">Report a new incident or schedule maintenance</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <Plus size={18} />
              New Incident
            </button>
          </div>
        </div>

        {/* Active Incidents */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Active Incidents</h2>
          {incidents.filter(i => i.status !== 'resolved').length === 0 ? (
            <div className="card text-center py-8">
              <CheckCircle2 className="mx-auto mb-3 text-green-500" size={32} />
              <p className="text-text-secondary">No active incidents. All systems operational!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {incidents.filter(i => i.status !== 'resolved').map(incident => (
                <IncidentCard
                  key={incident.id}
                  incident={incident}
                  services={services}
                  onAddUpdate={() => setShowUpdateModal(incident.id)}
                  onDelete={() => handleDeleteIncident(incident.id)}
                  formatDate={formatDate}
                  getImpactConfig={getImpactConfig}
                />
              ))}
            </div>
          )}
        </div>

        {/* Resolved Incidents */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Resolved Incidents</h2>
          <div className="space-y-4">
            {incidents.filter(i => i.status === 'resolved').slice(0, 10).map(incident => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                services={services}
                onAddUpdate={() => setShowUpdateModal(incident.id)}
                onDelete={() => handleDeleteIncident(incident.id)}
                formatDate={formatDate}
                getImpactConfig={getImpactConfig}
                collapsed
              />
            ))}
          </div>
        </div>

      {/* Create Incident Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border-default">
              <h2 className="text-xl font-semibold">Create New Incident</h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Incident Title</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g., API Response Delays"
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                />
              </div>

              {/* Impact */}
              <div>
                <label className="block text-sm font-medium mb-2">Impact Level</label>
                <div className="flex gap-2">
                  {impactOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => setFormImpact(option.value as Incident['impact'])}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                        formImpact === option.value
                          ? `${option.bg} ${option.color}`
                          : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Initial Status</label>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value as Incident['status'])}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Affected Services */}
              <div>
                <label className="block text-sm font-medium mb-2">Affected Services</label>
                <div className="flex flex-wrap gap-2">
                  {services.map(service => (
                    <button
                      key={service.id}
                      onClick={() => {
                        setFormServices(prev =>
                          prev.includes(service.id)
                            ? prev.filter(s => s !== service.id)
                            : [...prev, service.id]
                        );
                      }}
                      className={cn(
                        'px-3 py-1.5 rounded-lg text-sm transition-colors',
                        formServices.includes(service.id)
                          ? 'bg-accent text-white'
                          : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                      )}
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-2">Initial Update Message</label>
                <textarea
                  value={formMessage}
                  onChange={(e) => setFormMessage(e.target.value)}
                  placeholder="Describe what's happening..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateIncident}
                disabled={!formTitle || formServices.length === 0 || !formMessage}
                className="btn btn-primary"
              >
                Create Incident
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Update Modal */}
      {showUpdateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-bg-card border border-border-default rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-border-default">
              <h2 className="text-xl font-semibold">Add Update</h2>
            </div>
            <div className="p-6 space-y-4">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none"
                >
                  <option value="">Select status...</option>
                  <option value="Investigating">Investigating</option>
                  <option value="Identified">Identified</option>
                  <option value="Monitoring">Monitoring</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  value={updateMessage}
                  onChange={(e) => setUpdateMessage(e.target.value)}
                  placeholder="Provide an update..."
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-accent focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-border-default flex justify-end gap-3">
              <button
                onClick={() => setShowUpdateModal(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddUpdate(showUpdateModal)}
                disabled={!updateStatus || !updateMessage}
                className="btn btn-primary"
              >
                <Send size={16} />
                Post Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Incident Card Component
interface IncidentCardProps {
  incident: Incident;
  services: ServiceConfig[];
  onAddUpdate: () => void;
  onDelete: () => void;
  formatDate: (date: string) => string;
  getImpactConfig: (impact: string) => typeof impactOptions[0];
  collapsed?: boolean;
}

function IncidentCard({
  incident,
  services,
  onAddUpdate,
  onDelete,
  formatDate,
  getImpactConfig,
  collapsed = false,
}: IncidentCardProps) {
  const [isExpanded, setIsExpanded] = useState(!collapsed);
  const impactConfig = getImpactConfig(incident.impact);

  const getStatusIcon = () => {
    switch (incident.status) {
      case 'resolved':
        return <CheckCircle2 className="text-green-500" size={20} />;
      case 'monitoring':
        return <Clock className="text-blue-500" size={20} />;
      default:
        return <AlertTriangle className={impactConfig.color} size={20} />;
    }
  };

  return (
    <div className="card">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start gap-3">
          {getStatusIcon()}
          <div>
            <h3 className="font-semibold">{incident.title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={cn('text-xs px-2 py-0.5 rounded-full', impactConfig.bg, impactConfig.color)}>
                {impactConfig.label}
              </span>
              <span className="text-text-tertiary text-xs">
                {formatDate(incident.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {incident.status !== 'resolved' && (
            <button
              onClick={(e) => { e.stopPropagation(); onAddUpdate(); }}
              className="btn btn-secondary btn-sm"
            >
              <Edit3 size={14} />
              Update
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="btn btn-secondary btn-sm text-red-500"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-border-default">
          {/* Affected Services */}
          <div className="mb-4">
            <span className="text-text-tertiary text-xs uppercase tracking-wide">Affected Services</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {incident.affectedServices.map(serviceId => {
                const service = services.find(s => s.id === serviceId);
                return (
                  <span key={serviceId} className="text-xs px-2 py-1 rounded bg-bg-elevated">
                    {service?.name || serviceId}
                  </span>
                );
              })}
            </div>
          </div>

          {/* Updates Timeline */}
          <div>
            <span className="text-text-tertiary text-xs uppercase tracking-wide">Updates</span>
            <div className="mt-2 space-y-3">
              {incident.updates.map((update, i) => (
                <div key={update.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={cn(
                      'w-2 h-2 rounded-full mt-2',
                      update.status === 'Resolved' || update.status === 'Completed' ? 'bg-green-500' :
                      update.status === 'Investigating' ? 'bg-yellow-500' :
                      update.status === 'Identified' ? 'bg-orange-500' :
                      'bg-blue-500'
                    )} />
                    {i < incident.updates.length - 1 && (
                      <div className="w-px flex-1 bg-border-default mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn(
                        'font-medium text-sm',
                        update.status === 'Resolved' || update.status === 'Completed' ? 'text-green-500' :
                        update.status === 'Investigating' ? 'text-yellow-500' :
                        update.status === 'Identified' ? 'text-orange-500' :
                        'text-blue-500'
                      )}>
                        {update.status}
                      </span>
                      <span className="text-text-tertiary text-xs">
                        {formatDate(update.timestamp)}
                      </span>
                    </div>
                    <p className="text-text-secondary text-sm">{update.message}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
